import type { LayoutNode } from './types';
import { assertLayoutNodeExhaustive } from './layoutAssertNever';

/**
 * Nodes in a table header / row-label slot; `[]` when it is plain text or
 * absent. Layouts are untyped JSON from the database, so a slot the type says
 * is required can still be missing -- every walker over these slots normalises
 * through here rather than testing `typeof` itself.
 */
export function tableSlotNodes(
  slot: string | LayoutNode[] | undefined
): LayoutNode[] {
  return Array.isArray(slot) ? slot : [];
}

/** As `tableSlotNodes`, shaped as a child list for the structural walk. */
function tableTextSlotChildLists(
  slot: string | LayoutNode[] | undefined
): LayoutNode[][] {
  return Array.isArray(slot) ? [slot] : [];
}

/**
 * Structural child lists for a layout node (full tree — both `conditional` branches).
 * Leaf nodes return `[]`. Callers recurse into each inner array.
 */
export function getLayoutStructureChildLists(node: LayoutNode): LayoutNode[][] {
  switch (node.type) {
    case 'card':
    case 'stack':
    case 'grid':
    case 'flex':
    case 'box':
    case 'indent':
    case 'disabled-when':
    case 'repeat':
      return [node.children];
    case 'field-row':
      return [node.label, node.controls];
    case 'conditional': {
      const lists: LayoutNode[][] = [node.children];
      if (node.elseChildren) lists.push(node.elseChildren);
      return lists;
    }
    case 'table':
      // Each cell is its own one-item list, so cell-to-cell adjacency
      // within a row is invisible to callers that walk sibling lists --
      // they can only see "before/after the whole table". Model
      // `row.cells` as one real sibling list if a caller ever needs it.
      //
      // Headers and row labels can hold nodes too, and a field placed there is
      // as real as one in a cell -- it has to count for coverage and be seen by
      // the hidden-QID and gate walkers -- so they join the same list.
      return [
        ...tableTextSlotChildLists(node.rowHeader),
        ...(node.colHeaders ?? []).flatMap(tableTextSlotChildLists),
        ...node.rows.flatMap(row => [
          ...tableTextSlotChildLists(row.label),
          ...row.cells.map(cell => [cell]),
        ]),
      ];
    case 'OASISMultiselect':
      return node.children ? [node.children] : [];
    case 'divider':
    case 'static':
    case 'text':
    case 'display':
    case 'external-answer':
    case 'section-label':
    case 'sum':
    case 'OASISRadio':
    case 'OASISYesNoCheckbox':
    case 'OASISSelect':
    case 'OASISText':
    case 'OASISPhysician':
    case 'OASISDate':
    case 'OASISTime':
    case 'OASISNumber':
    case 'OASISMultiselectOption':
    case 'radio-option':
      return [];
    default:
      return assertLayoutNodeExhaustive(node);
  }
}

export function walkLayoutStructureChildren(
  node: LayoutNode,
  walk: (nodes: LayoutNode[]) => void
): void {
  for (const list of getLayoutStructureChildLists(node)) {
    walk(list);
  }
}
